import React from 'react'
import { ButtonBase, Portal, alpha, type SxProps, type Theme } from '@mui/material'
import { ChevronLeft } from 'lucide-react'

import { authPx, authScaleSx } from '../authScale'

/**
 * THE AUTH CHROME TREATMENT — one definition for the two quiet controls that
 * frame every auth screen: the light/dark toggle (top-right, LayoutAuthAmbient)
 * and Back (top-left, and inline "back" controls inside the card).
 *
 * Spencer, 2026-09-23: "Back button should be a text button like the
 * light/dark mode selector — on all sign-in / sign-up and /enter pages."
 * Before this, sign-up's Back was a `@repo/ui` text Button in `text.secondary`
 * that went BOLD on hover with no plate, while the toggle was a 56%-ink icon
 * with a 6% plate on hover. Two treatments for two controls that sit in
 * mirrored corners of the same screen. Shared here — and imported by the
 * toggle — so the pair cannot drift again.
 *
 *   rest   color alpha(text.primary, .56), no plate, weight 400
 *   hover  color text.primary, plate alpha(text.primary, .06), weight 400
 *   box    36px tall, 8px radius (the toggle's measured radius, pinned here
 *          rather than inherited so both are 8px whatever IconButton's theme
 *          override does)
 *
 * Every length here is `authPx(n)`, i.e. it follows `--auth-scale`
 * (authScale.ts): the sizes above times whatever that resolves to. The factor is
 * not named here on purpose — it was 0.8, is 1 since VRG-784, and a constant
 * copied into prose goes stale silently.
 *
 * Keyboard focus gets the hover look too (`.Mui-focusVisible`), so a tabbing
 * user sees where they are without a second visual language.
 */
export const authChromeControlSx = (theme: Theme) =>
  ({
    height: authPx(36),
    borderRadius: authPx(8),
    color: alpha(theme.palette.text.primary, 0.56),
    transition: theme.transitions.create(['color', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover, &.Mui-focusVisible': {
      color: theme.palette.text.primary,
      bgcolor: alpha(theme.palette.text.primary, 0.06),
    },
  }) as const

/**
 * The corner placement shared by the toggle (right) and Back (left).
 *
 * ⚠️ zIndex 20, NOT lower. Sign-up's page shell is `position: fixed;
 * z-index: 10` over the whole viewport; at 3 the toggle was VISIBLE BUT
 * UNCLICKABLE there (hit-tested 2026-09-23: elementFromPoint at the corner
 * returned sign-up's Stack). Sign-in's shell sets no z-index, which is why it
 * only ever broke on sign-up.
 *
 * THE PAIR SCALES AS ONE (Spencer, 2026-10-09: "the light/dark button in the
 * top right scales, but the back button in the top left doesn't"). Each corner
 * sets the desktop `--auth-scale` on ITSELF (`authScaleSx`), because neither
 * sits inside a page shell that could hand it down: the toggle is rendered by
 * LayoutAuthAmbient, and Back is portaled. Same box, same 16px (x scale) inset
 * from its two edges, on every auth page, so the pair cannot jump size when
 * sign-in hands over to sign-up.
 */
export const authCornerControlSx = (theme: Theme, side: 'left' | 'right') =>
  ({
    ...authScaleSx,
    ...authChromeControlSx(theme),
    position: 'fixed',
    top: authPx(16),
    [side]: authPx(16),
    zIndex: 20,
    // Icons are lucide SVGs with a fixed `size` attribute; CSS sizes them.
    '& svg': { width: authPx(18), height: authPx(18), flexShrink: 0 },
  }) as const

/** The app mount root, where the text zoom lives; <body> if it is missing (tests). */
const appRoot = () => document.getElementById('app') ?? document.body

/** Exactly one of `onClick` or `href` — same union as `AuthInlineLink`. */
export type AuthBackButtonProps = {
  /** Visible label. Defaults to "Back". */
  label?: string
  /**
   * `corner` — fixed in the screen's top-left, portaled to <body>.
   * `inline` — in flow inside the card, optically flush with the column edge.
   */
  placement?: 'corner' | 'inline'
  sx?: SxProps<Theme>
} & ({ onClick: () => void; href?: never } | { href: string; onClick?: never })

/**
 * Back, in the auth chrome treatment above: chevron + label, regular weight at
 * rest AND on hover (the old bold-on-hover, and the hidden `::after` bold copy
 * that reserved its width, are gone with it).
 *
 * PADDING IS OPTICAL, NOT SYMMETRIC. Lucide's chevron-left ink spans x 9-15 of
 * its 24 box, i.e. 6.75-11.25px at 18px. With `pl 4px / gap 2px / pr 12px` the
 * visible margins are 4 + 6.75 = 10.75px left of the ink, 6.75 + 2 = 8.75px
 * between ink and label, 12px right of the label — balanced around the plate.
 * The old `px 10 / gap 8` put 16.75px of air left of the ink and 14.75px
 * between chevron and word, so the plate looked lopsided and the chevron read
 * as detached.
 *
 * CORNER placement is PORTALED, and that is load-bearing: both auth form
 * columns animate with `transform` on enter/exit, and a transformed ancestor
 * becomes the containing block for `position: fixed` — left in place, the
 * button would ride along with the column.
 *
 * ⚠️ PORTALED INTO `#app`, NOT <body> (2026-10-09). The app's text zoom
 * (hooks/useTextZoom, Cmd -/+) is `#app { zoom }`, and the theme toggle in the
 * opposite corner renders inside `#app`. Portaled to <body>, Back sat OUTSIDE
 * the zoom: at Cmd - the toggle shrank to 29px and moved in to 13px while Back
 * stayed 36px at 16px, which is the "the back button doesn't scale" report.
 * Inside `#app` both corners get the same zoom. The reason `useTextZoom` keeps
 * MUI popovers out of `#app` (anchor maths read in visual px, written back in
 * zoomed px) does not apply here: this position is a constant, not read from
 * an anchor. `#app` has no transform, so it is not a containing block.
 *
 * `href` renders a real anchor (same tab) — used by sign-in's entry step to go
 * back out to vera.inc, which is where its visitors come from.
 */
export const AuthBackButton = ({ label = 'Back', placement = 'corner', sx, ...action }: AuthBackButtonProps) => {
  const button = (
    <ButtonBase
      {...(action.href !== undefined
        ? { LinkComponent: 'a', href: action.href }
        : { onClick: action.onClick, type: 'button' as const })}
      sx={[
        (theme) => ({
          ...(placement === 'corner'
            ? authCornerControlSx(theme, 'left')
            : {
                ...authChromeControlSx(theme),
                // Inline: hug the label rather than stretch across a column
                // Stack, and pull left by the chevron's own left inset + pl
                // (4 + 6.75 ≈ 11px) so its INK sits on the column's edge the
                // way the text around it does; the plate bleeds into the gutter.
                alignSelf: 'flex-start',
                ml: authPx(-11),
                '& svg': { width: authPx(18), height: authPx(18), flexShrink: 0 },
              }),
          display: 'inline-flex',
          alignItems: 'center',
          gap: authPx(2),
          pl: authPx(4),
          pr: authPx(12),
          fontFamily: theme.typography.fontFamily,
          fontSize: authPx(16),
          fontWeight: 400,
          lineHeight: 1,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ChevronLeft size={18} strokeWidth={1.75} aria-hidden />
      {label}
    </ButtonBase>
  )

  return placement === 'corner' ? <Portal container={appRoot}>{button}</Portal> : button
}

export default AuthBackButton
